import {HttpClient} from '@angular/common/http';
import {Inject, Injectable} from '@angular/core';

import {Observable} from 'rxjs';
import {AgentModel} from "../models/agent.model";
import {Document} from "../../../commons/models/document.model";
import {Reminder} from "../../../commons/models/reminder.model";
import {AgentsPaginationQueryModel} from "../models/agents-pagination-query.model";
import {AgentsPaginationDataModel} from "../models/agents-pagination-data.model";
import {AgentItineraryModel} from "../models/agent-itinerary.model";
import {AgentAuthType} from "../../types/agentAuth.type";
import {AgentClientType} from "../../types/agentClient.type";
import {AgentType} from "../../types/agent.type";
import {AgentStatusType} from "../../types/agentStatus.type";
import {ResourceSkill} from "../../types/resourceSkill.type";
import {GpsPermissionType} from "../../types/gps-permission.type";
import {TotalsModel} from "../../../commons/models/totals.model";
import {TaskModel} from "../../../tasks/models/task.model";

import {AgentPreferencesModel} from "../models/agent-preferences.model";
import {AgentBalanceModel, PaymentTransactionModel} from "../models/payment-transaction.model";
import {AgentTotalsResponseModel, AgentEngagementResponseModel, AgentRequirementsResponseModel, AgentAgreementsResponseModel, AgentBulkUpdateResponseModel, AgentPerformanceResponseModel, AgentPasswordlessResponseModel} from "../models/response.model";

@Injectable({providedIn: 'root'})
export class AgentsHttpService {

  private base_url = `/agents`;

  constructor(@Inject(HttpClient) private http: HttpClient) {
  }

  getAgents(params?: any): Observable<{ list: AgentModel[], cursor?: string }> {
    return this.http
      .get<{ list: AgentModel[], cursor?: string }>(this.base_url, {params});
  }

  getOne(agentId: string): Observable<AgentModel> {
    return this.http
      .get<AgentModel>(`${this.base_url}/${agentId}`)
  }

  createOne(agent: AgentModel): Observable<AgentModel> {
    return this.http
      .put<AgentModel>(this.base_url, agent)
  }

  updateOne(agentId: string, agent: AgentModel): Observable<AgentModel> {
    return this.http
      .post<AgentModel>(`${this.base_url}/${agentId}`, agent)
  }

  deleteOne(agentId: string, shouldMask: boolean): Observable<AgentModel> {
    return this.http
      .delete<AgentModel>(`${this.base_url}/${agentId}`, {params: {shouldMask: shouldMask}})
  }

  // ==================== PAGINATION & FILTERING ====================

  getAgentsPagination(queryPayload: AgentsPaginationQueryModel): Observable<AgentsPaginationDataModel> {
    return this.http
      .get<AgentsPaginationDataModel>(`${this.base_url}/paginated`, {params: queryPayload as any});
  }

  getAgentsByBranch(branchIds: string[]): Observable<{ list: AgentModel[], cursor?: string }> {
    return this.http
      .get<{ list: AgentModel[], cursor?: string }>(this.base_url, {params: {branchIds: branchIds}});
  }

  getAgentTotals(query?: {[key: string]: any}): Observable<AgentTotalsResponseModel> {
    return this.http
      .get<AgentTotalsResponseModel>(`${this.base_url}/totals/statuses`, {params: query});
  }

  getAgentTotalsByConfigurationId(configurationId: string): Observable<AgentTotalsResponseModel> {
    return this.http
      .get<AgentTotalsResponseModel>(`${this.base_url}/totals`, {params: {configurationId: configurationId}});
  }

  // ==================== PREFERENCES & CONFIGURATION ====================

  getAgentPreferences(agentId: string): Observable<AgentPreferencesModel> {
    return this.http
      .get<AgentPreferencesModel>(`${this.base_url}/${agentId}/preferences`);
  }

  setAgentPreferences(agentId: string, preferences: Partial<AgentPreferencesModel>): Observable<AgentPreferencesModel> {
    return this.http
      .post<AgentPreferencesModel>(`${this.base_url}/${agentId}/preferences`, preferences);
  }

  assignAgentsToProfile(profileId: string, agentIds: string[]): Observable<AgentBulkUpdateResponseModel> {
    return this.http
      .post<AgentBulkUpdateResponseModel>(`${this.base_url}/assignConfigurationProfile`, {ids: agentIds}, {params: {profileId: profileId}});
  }

  bulkPreferencesUpdate(preferences: Partial<AgentPreferencesModel>, selectedIds: string[], excludedIds?: string[], branchIds?: string[],
                       isActive?: boolean, sessionRecording?: boolean, status?: AgentStatusType, agentType?: AgentType,
                       agentSkills?: ResourceSkill[], gpsStatus?: boolean, gpsPermissions?: GpsPermissionType[]): Observable<AgentBulkUpdateResponseModel> {
    return this.http
      .post<AgentBulkUpdateResponseModel>(`${this.base_url}/bulk`, preferences, {
        params: {
          ids: selectedIds,
          excludedIds: excludedIds,
          branchIds: branchIds,
          active: isActive,
          sessionRecording: sessionRecording,
          status: status,
          agentTypes: agentType,
          skill: agentSkills,
          gpsStatus: gpsStatus,
          gpsPermission: gpsPermissions
        }
      });
  }


  changeAgentsStatus(status: AgentStatusType, agentIds: string[]): Observable<AgentBulkUpdateResponseModel> {
    return this.http
      .post<AgentBulkUpdateResponseModel>(`${this.base_url}/actions/changeStatus/${status}`, {ids: agentIds});
  }

  updatePassword(agentId: string, password: string): Observable<AgentModel> {
    return this.http
      .post<AgentModel>(`${this.base_url}/${agentId}/password`, {password: password});
  }

  updateCarrier(agentId: string, carrierId: string): Observable<AgentModel> {
    return this.http
      .post<AgentModel>(`${this.base_url}/${agentId}/updateCarrier`, {carrierId: carrierId});
  }

  updateAgentLocation(agentId: string, lat: number, lng: number): Observable<AgentModel> {
    return this.http
      .post<AgentModel>(`${this.base_url}/${agentId}/location`, {lat: lat, lng: lng});
  }

  // ==================== ITINERARY MANAGEMENT ====================

  getItinerary(agentId: string): Observable<AgentItineraryModel> {
    return this.http
      .get<AgentItineraryModel>(`${this.base_url}/${agentId}/itinerary`);
  }

  refreshAgentItinerary(agentId: string, ignoreTimeWindow: boolean): Observable<AgentItineraryModel> {
    return this.http
      .get<AgentItineraryModel>(`${this.base_url}/${agentId}/itinerary/refresh`, {params: {ignoreTimeWindow: ignoreTimeWindow}});
  }

  recalcAgentItinerary(agentId: string): Observable<AgentItineraryModel> {
    return this.http
      .get<AgentItineraryModel>(`${this.base_url}/${agentId}/itinerary/recalc`);
  }

  editItineraryLock(agentId: string): Observable<AgentItineraryModel> {
    return this.http
      .post<AgentItineraryModel>(`${this.base_url}/${agentId}/itinerary/edit/lock`, {});
  }

  editItineraryUnlock(agentId: string): Observable<AgentItineraryModel> {
    return this.http
      .post<AgentItineraryModel>(`${this.base_url}/${agentId}/itinerary/edit/unlock`, {});
  }

  checkEditItinerary(agentId: string, payload: Partial<AgentItineraryModel>): Observable<AgentItineraryModel> {
    return this.http
      .post<AgentItineraryModel>(`${this.base_url}/${agentId}/itinerary/edit`, payload, {params: {save: false}});
  }

  saveEditItinerary(agentId: string, payload: Partial<AgentItineraryModel>): Observable<AgentItineraryModel> {
    return this.http
      .post<AgentItineraryModel>(`${this.base_url}/${agentId}/itinerary/edit`, payload, {params: {save: true}});
  }

  editItineraryReset(agentId: string): Observable<AgentItineraryModel> {
    return this.http
      .post<AgentItineraryModel>(`${this.base_url}/${agentId}/itinerary/edit/reset`, {});
  }

  editItinerarySettings(agentId: string, itinerary: Partial<AgentItineraryModel>): Observable<AgentItineraryModel> {
    return this.http
      .post<AgentItineraryModel>(`${this.base_url}/${agentId}/itinerary/edit/settings`, itinerary);
  }


  getEngagement(agentId: string): Observable<AgentEngagementResponseModel> {
    return this.http
      .get<AgentEngagementResponseModel>(`${this.base_url}/${agentId}/engagement`);
  }

  getRequirements(agentId: string): Observable<AgentRequirementsResponseModel> {
    return this.http
      .get<AgentRequirementsResponseModel>(`${this.base_url}/${agentId}/requirements`);
  }

  postRequirements(agentId: string, requirement: {id: string; [key: string]: any}, sendSms: boolean, sendEmail: boolean): Observable<AgentRequirementsResponseModel> {
    return this.http
      .post<AgentRequirementsResponseModel>(`${this.base_url}/${agentId}/requirements/${requirement.id}`, requirement, {
        params: {
          sendSms: sendSms,
          sendEmail: sendEmail
        }
      });
  }

  getAgreements(agentId: string): Observable<AgentAgreementsResponseModel> {
    return this.http
      .get<AgentAgreementsResponseModel>(`${this.base_url}/${agentId}/agreements`);
  }


  getDocuments(agentId: string): Observable<Document[]> {
    return this.http
      .get<Document[]>(`${this.base_url}/${agentId}/document`);
  }

  createDocument(agentId: string, document: Document): Observable<Document> {
    return this.http
      .put<Document>(`${this.base_url}/${agentId}/document`, document);
  }

  updateDocument(agentId: string, document: Document): Observable<Document> {
    return this.http
      .post<Document>(`${this.base_url}/${agentId}/document/${document.id}`, document);
  }

  deleteDocument(agentId: string, documentId: string): Observable<Document> {
    return this.http
      .delete<Document>(`${this.base_url}/${agentId}/document/${documentId}`);
  }


  getReminders(agentId: string): Observable<Reminder[]> {
    return this.http
      .get<Reminder[]>(`${this.base_url}/${agentId}/reminder`);
  }

  createReminder(agentId: string, reminder: Reminder): Observable<Reminder> {
    return this.http
      .put<Reminder>(`${this.base_url}/${agentId}/reminder`, reminder);
  }

  updateReminder(agentId: string, reminder: Reminder): Observable<Reminder> {
    return this.http
      .post<Reminder>(`${this.base_url}/${agentId}/reminder/${reminder.id}`, reminder);
  }

  deleteReminder(agentId: string, reminderId: string): Observable<Reminder> {
    return this.http
      .delete<Reminder>(`${this.base_url}/${agentId}/reminder/${reminderId}`);
  }


  addAgentsToTeam(teamIds: string[], agentIds: string[]): Observable<AgentBulkUpdateResponseModel> {
    return this.http
      .post<AgentBulkUpdateResponseModel>(`${this.base_url}/addTeams`, {teamIds, agentIds});
  }

  removeAgentsFromTeam(teamIds: string[], agentIds: string[]): Observable<AgentBulkUpdateResponseModel> {
    return this.http
      .post<AgentBulkUpdateResponseModel>(`${this.base_url}/removeTeams`, {teamIds, agentIds});
  }

  getAgentAssistants(agentId: string): Observable<AgentModel[]> {
    return this.http
      .get<AgentModel[]>(`${this.base_url}/${agentId}/assistants`);
  }

  getAssistants(limit: number, query?: string, cursor?: string): Observable<{ list: AgentModel[], cursor?: string }> {
    let params: any = { limit: limit };
    if (query) {
      params.query = query;
    }
    if (cursor) {
      params.cursor = cursor;
    }

    return this.http.get<{ list: AgentModel[], cursor?: string }>(`${this.base_url}`, { params });
  }

  getAssistantsForRoutes(routeIds: string[], limit: number, query?: string, cursor?: string): Observable<{ list: AgentModel[], cursor?: string }> {
    let params: any = { limit: limit };
    if (query) {
      params.query = query;
    }
    if (cursor) {
      params.cursor = cursor;
    }

    // To pass multiple routeIds as ?routeIds=1&routeIds=2 we can set an array directly in params
    params.routeIds = routeIds;

    return this.http.get<{ list: AgentModel[], cursor?: string }>(`${this.base_url}/assistants/routes`, { params });
  }

  updateAgentAssistants(agent: AgentModel): Observable<AgentModel> {
    return this.http
      .post<AgentModel>(`${this.base_url}/${agent.id}/assistants`, agent);
  }


  getAgentBalance(agentId: string): Observable<AgentBalanceModel> {
    return this.http
      .get<AgentBalanceModel>(`${this.base_url}/${agentId}/wallet/balance`);
  }

  addPaymentTransaction(agentId: string, transaction: PaymentTransactionModel): Observable<PaymentTransactionModel> {
    return this.http
      .post<PaymentTransactionModel>(`${this.base_url}/${agentId}/wallet`, transaction);
  }

  getAgentPaymentCollectionTotals(agentId: string, from?: number, to?: number, transactionType?: string, accountId?: string): Observable<TotalsModel> {
    return this.http
      .get<TotalsModel>(`${this.base_url}/${agentId}/wallet/totals`, {
        params: {from: from, to: to, type: transactionType, accountId: accountId}
      });
  }

  getAllAgentsPaymentTotals(from?: number, to?: number, transactionType?: string, accountId?: string): Observable<TotalsModel> {
    return this.http
      .get<TotalsModel>(`${this.base_url}/wallet/totals`, {
        params: {from: from, to: to, type: transactionType, accountId: accountId}
      });
  }


  addDepots(depotIds: string[], agentIds: string[]): Observable<AgentBulkUpdateResponseModel> {
    return this.http
      .post<AgentBulkUpdateResponseModel>(`${this.base_url}/addDepots`, {depotIds: depotIds, agentIds: agentIds});
  }

  addBranches(branchIds: string[], agentIds: string[]): Observable<AgentBulkUpdateResponseModel> {
    return this.http
      .post<AgentBulkUpdateResponseModel>(`${this.base_url}/addBranches`, {branchIds: branchIds, agentIds: agentIds});
  }

  // ==================== SCHEDULE MANAGEMENT ====================

  selectScheduleTemplate(agentId: string, scheduleTemplateId: string): Observable<AgentModel> {
    return this.http
      .post<AgentModel>(`${this.base_url}/${agentId}/selectSchedule`, {}, {params: {selectedScheduleId: scheduleTemplateId}});
  }

  clearSchedule(agentId: string): Observable<AgentModel> {
    return this.http
      .post<AgentModel>(`${this.base_url}/${agentId}/clearSchedule`, {});
  }


  exportPending(): Observable<Blob> {
    return this.http
      .get(`${this.base_url}/actions/export/pending`, {responseType: 'blob'});
  }

  exportAgents(exportType: string, status?: AgentStatusType, agentType?: AgentType, branchIds?: string[],
              noBranches?: boolean, isActive?: boolean, sessionRecording?: boolean,
              areaIds?: string[], gpsStatus?: boolean, gpsPermissions?: GpsPermissionType[]): Observable<Blob> {
    return this.http
      .post(`${this.base_url}/actions/export`, {}, {
        responseType: 'blob',
        params: {
          exportType: exportType,
          branchIds: branchIds,
          noBranches: noBranches,
          status: status,
          agentType: agentType,
          isActive: isActive,
          sessionRecording: sessionRecording,
          areaIds: areaIds,
          gpsStatus: gpsStatus,
          gpsPermission: gpsPermissions
        }
      });
  }

  sendNotification(agentPnType: string, selectedIds?: string[], excludedIds?: string[], branchIds?: string[],
                  noBranches?: boolean, status?: AgentStatusType, agentType?: AgentType, agentSkills?: ResourceSkill[],
                  gpsStatus?: boolean, gpsPermissions?: GpsPermissionType[], isActive?: boolean,
                  sessionRecording?: boolean, noArea?: boolean, serviceAreaIds?: string[]): Observable<AgentBulkUpdateResponseModel> {
    return this.http
      .post<AgentBulkUpdateResponseModel>(`${this.base_url}/actions/sendNotification`, {agentPnType}, {
        params: {
          ids: selectedIds,
          excludedIds: excludedIds,
          branchIds: branchIds,
          noBranches: noBranches,
          status: status,
          agentType: agentType,
          skill: agentSkills,
          gpsStatus: gpsStatus,
          gpsPermission: gpsPermissions,
          isActive: isActive,
          sessionRecording: sessionRecording,
          noArea: noArea,
          areaIds: serviceAreaIds
        }
      });
  }

  passwordless(agentId: string, authType: AgentAuthType, client: AgentClientType): Observable<AgentPasswordlessResponseModel> {
    return this.http
      .post<AgentPasswordlessResponseModel>(`${this.base_url}/${agentId}/passwordless`, {}, {params: {authType: authType, client: client}});
  }

  getAgentPerformance(agentId: string, from: number, to: number): Observable<AgentPerformanceResponseModel> {
    return this.http
      .get<AgentPerformanceResponseModel>(`${this.base_url}/${agentId}/performance`, {params: {from: from, to: to}});
  }

  updateAgentExternalId(agent: AgentModel): Observable<AgentModel> {
    return this.http
      .post<AgentModel>(`${this.base_url}/${agent.id}/externalId`, agent);
  }

  getAgentCrossSiteData(email: string, id: string): Observable<AgentModel[]> {
    return this.http
      .post<AgentModel[]>(`${this.base_url}/agentCrossSiteData`, {email, id});
  }

  getAgentCompletedTaskByDate(agentId: string, timestamp: number, zoneId: string): Observable<TaskModel[]> {
    return this.http
      .get<TaskModel[]>(`${this.base_url}/${agentId}/tasks`, {params: {date: timestamp, zoneId: zoneId, onlyCompleted: false}});
  }
}
